import AiAllowance from './AiAllowance.jsx';
// src/components/MyEssaysPanel.jsx
//
// The "My Essays" Account panel -- owner-only draft snapshots and graded essays.
// Drafts have aiFeedback.status='draft', never a mark sheet or progress grade.
// Reads /users/{uid}/essayAttempts via useEssayAttempts
// (owner-only), newest first; each row expands to a read-only render of the essay +
// its AI feedback. Per-attempt delete (user-deletable, immutable otherwise).
//
// Mirrors MyTutorReviewPanel (owner-read list + per-item expand + midnight theme).
// The feedback render is the SHARED MarkSheet (lesson-3c); the row summary still uses
// the practice tiers so the band/confidence wording stays identical to live grading.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AUTH_MODE, auth } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { useEssayAttempts, useEssayAttemptWriter } from '../storage/useEssayAttempts.js';
import { useEntitlements } from '../storage/useEntitlements.js';
import { sortAttemptsNewestFirst, MAX_ESSAY_CHARS } from '../storage/essayAttemptHelpers.js';
import { summarizeProgress } from '../storage/essayProgress.js';
import Sparkline from './practice/Sparkline.jsx';
import { formatBandRange, failureMessage, diagnosticReference } from './practice/aiFeedbackMessages.js';
import { isEssayDraft } from '../storage/essayDrafts.js';
import MarkSheet from './practice/MarkSheet.jsx';
import { gradeOwnEssay, listGradableEntitledPacks, activateGradingTrial } from '../lib/callables.js';
import { IconSparkles } from './icons.jsx';
import { sendEmailVerification } from 'firebase/auth';

// Mirrors the CF caps (MAX_ESSAY_PROMPT_CHARS server-side; essay = rules cap).
const MAX_QUESTION_CHARS = 6000;

function fmt(ts) {
    if (!ts) return '—';
    const d = typeof ts.toDate === 'function' ? ts.toDate()
        : typeof ts.seconds === 'number' ? new Date(ts.seconds * 1000)
        : new Date(ts);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB');
}

// Compact date-only variant for the progress rows (same tolerant conversion).
function fmtDate(ts) {
    if (!ts) return '—';
    const d = typeof ts.toDate === 'function' ? ts.toDate()
        : typeof ts.seconds === 'number' ? new Date(ts.seconds * 1000)
        : new Date(ts);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB');
}

// "Your Progress" (essay-progress-beforeafter slice): each student's own before ->
// after per redrafted prompt (first parseable grade -> latest), derived purely from
// the attempts already loaded by useEssayAttempts -- no extra reads. Wellbeing
// framing: gains celebrated; flat/down rendered NEUTRALLY (muted, no red, no shame
// copy -- grades here are indicative + formative, a dip must never read as failure).
function YourProgressCard({ attempts }) {
    if (!attempts || attempts.length === 0) return null; // brand-new: the history empty state covers it
    const { redraftedCount, improvedCount, bestGain, groupsWithProgress } = summarizeProgress(attempts);

    if (groupsWithProgress.length === 0) {
        // Attempts exist but nothing has 2 gradeable marks yet -> a soft nudge,
        // never an empty-feeling block.
        return (
            <div className="border border-midnight-accent/40 bg-midnight-page/60 p-6 decorative-corner text-midnight-accent">
                <div className="font-display text-midnight-accent text-xs mb-2 font-semibold">Your Progress</div>
                <p className="font-serif italic text-midnight-muted text-sm leading-relaxed">
                    Apply the feedback, then compare your next draft with the previous one. Task marks and feedback are saved below; they are not converted into qualification grades.
                </p>
            </div>
        );
    }

    return (
        <div className="border border-midnight-accent/40 bg-midnight-page/60 p-6 decorative-corner text-midnight-accent">
            <div className="font-display text-midnight-accent text-xs mb-2 font-semibold">Your Progress</div>
            {improvedCount > 0 && bestGain ? (
                <>
                    <p className="font-serif text-midnight-text text-base leading-relaxed">
                        Your biggest gain so far: <span className="font-display text-midnight-accent">{bestGain.from} → {bestGain.to}</span> — &ldquo;{bestGain.title}&rdquo;.
                    </p>
                    <p className="font-serif italic text-midnight-muted text-xs mt-1">
                        Improved on {improvedCount} of {redraftedCount} redrafted essay{redraftedCount === 1 ? '' : 's'}.
                    </p>
                </>
            ) : (
                <p className="font-serif italic text-midnight-muted text-sm leading-relaxed">
                    You&rsquo;re building a redrafting habit — {redraftedCount} essay{redraftedCount === 1 ? '' : 's'} tracked so far.
                    Keep applying your next steps; growth shows here as your grades move.
                </p>
            )}

            <div className="mt-4 space-y-2">
                {groupsWithProgress.map((g) => {
                    const p = g.progress;
                    const up = p.delta > 0;
                    return (
                        <div key={g.key} className="border-t border-midnight-accent/10 pt-2">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                <span className="font-serif text-midnight-text text-sm min-w-0 flex-1 truncate">{g.title}</span>
                                {/* lesson-3c: the shape of the journey, not just its ends. */}
                                <Sparkline series={p.series} rising={up} />
                                <span className={`font-display text-sm whitespace-nowrap ${up ? 'text-midnight-accent' : 'text-midnight-muted'}`}>
                                    {p.first.band} → {p.latest.band}{up && <span aria-hidden="true"> ↑</span>}
                                </span>
                                <span className="font-serif italic text-midnight-muted text-xs whitespace-nowrap">
                                    {p.attemptCount} draft{p.attemptCount === 1 ? '' : 's'} · {fmtDate(p.first.takenAt)} → {fmtDate(p.latest.takenAt)}
                                </span>
                            </div>
                            {p.delta === 0 && (
                                <p className="font-serif italic text-midnight-muted text-xs mt-0.5">
                                    steady — try the next steps from your feedback
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="font-serif italic text-midnight-muted text-xs border-t border-midnight-accent/15 pt-3 mt-4">
                This chart shows earlier indicative AI grades only. New task-specific marks are saved below and are not comparable with this grade scale.
            </p>
        </div>
    );
}

// Read-only render of a stored aiFeedback map (same field order as the live tiers).
// lesson-3c: the SAME MarkSheet the live feedback tiers render. The old bespoke copy
// of the layout is gone, so the history view and the live view cannot drift apart.
// MarkSheet uses book-* classes, which alias the same semantic tokens as midnight-*
// (index.css), so it sits correctly inside the Account page.
function FeedbackView({ fb }) {
    if (!fb || fb.status === 'draft') return null;
    return (
        <div className="mt-3 border-t border-midnight-accent/15 pt-3">
            <MarkSheet fb={fb} />
        </div>
    );
}

// "Mark a new essay" (own-essay-ai-grading slice): entitled users paste their
// OWN exam question + essay and grade it against a purchased pack's rubric.
// Uses the SHARED daily grade budget (combined with in-pack marking).
function MarkNewEssayBox() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [trial, setTrial] = useState(null);
    const [agree, setAgree] = useState(false);
    const [activating, setActivating] = useState(false);
    const [trialError, setTrialError] = useState('');
    const [revision, setRevision] = useState(0);
    const { saveOwnAttempt, saveDraft } = useEssayAttemptWriter();
    const [draftState, setDraftState] = useState('idle');
    const [diagnosticId, setDiagnosticId] = useState('');
    // Client entitlement listener: splits the zero-gradable state into "owns
    // nothing" (upsell) vs "owns packs but none has grading enabled" (no upsell).
    const { activePackIds } = useEntitlements();
    const entitlementKey = [...activePackIds].sort().join(',');

    const [pickerState, setPickerState] = useState('loading'); // loading | ready | error
    const [gradable, setGradable] = useState([]);
    const [dailyCap, setDailyCap] = useState(null); // live shared cap from the CF (env-overridable)
    const [rubricPackId, setRubricPackId] = useState('');
    const [promptText, setPromptText] = useState('');
    const [essayText, setEssayText] = useState('');
    const [gradeState, setGradeState] = useState('idle'); // idle | grading | done | error
    const [result, setResult] = useState(null);
    const [failReason, setFailReason] = useState(null);
    const [remaining, setRemaining] = useState(null);
    const [submitted, setSubmitted] = useState(null); // snapshot of what was actually graded

    useEffect(() => {
        let cancelled = false;
        listGradableEntitledPacks()
            .then((res) => {
                if (cancelled) return;
                const packs = (res && Array.isArray(res.packs)) ? res.packs : [];
                setGradable(packs);
                setTrial(res?.trial || null);
                if (Number.isInteger(res && res.dailyCap)) setDailyCap(res.dailyCap);
                if (packs.length === 1) setRubricPackId(packs[0].packId); // pre-select the only choice
                setPickerState('ready');
            })
            .catch((e) => { if (!cancelled) { console.error('listGradableEntitledPacks:', e); setPickerState('error'); } });
        return () => { cancelled = true; };
    }, [revision, entitlementKey]);

    const startTrial = async () => {
        setActivating(true); setTrialError('');
        try {
            await currentUser?.reload();
            await currentUser?.getIdToken(true);
            await activateGradingTrial({ agree });
            setRevision(r => r + 1);
        } catch (e) { setTrialError(e.message || 'Could not start the trial.'); }
        finally { setActivating(false); }
    };
    const verifyEmail = async () => {
        setActivating(true);
        try { await sendEmailVerification(currentUser); setTrialError('Verification email sent. Open the link, then return and start your trial.'); }
        catch (e) { setTrialError(e.message || 'Could not send the verification email.'); }
        finally { setActivating(false); }
    };

    const canMark = rubricPackId && promptText.trim() && essayText.trim()
        && promptText.length <= MAX_QUESTION_CHARS && essayText.length <= MAX_ESSAY_CHARS
        && gradeState !== 'grading';

    const mark = async () => {
        if (!canMark) return;
        setGradeState('grading'); setFailReason(null); setResult(null); setDiagnosticId('');
        const snapshot = { rubricPackId, promptText, essayText };
        const owner = currentUser?.uid;
        try {
            setDraftState('saving');
            const pack = gradable.find(p => p.packId === snapshot.rubricPackId);
            try { await saveDraft({ ...snapshot, source: 'own', packLabel: pack?.label || '' }); }
            catch { setDraftState('error'); setFailReason('draft_save_failed'); setGradeState('error'); return; }
            if (auth?.currentUser?.uid !== owner) return;
            setDraftState('saved');
            const res = await gradeOwnEssay(snapshot);
            if (auth?.currentUser?.uid !== owner) return;
            // remainingToday can ride on failures too (e.g. malformed output
            // keeps its consumed slot) -- always sync the "left today" count.
            if (res && Number.isInteger(res.remainingToday)) setRemaining(res.remainingToday);
            if (res && res.status === 'graded') {
                if (Number.isInteger(res.trialRemaining)) setTrial(t => ({ ...t, remaining: res.trialRemaining }));
                setResult(res); setSubmitted(snapshot); setGradeState('done');
                const pack = gradable.find((p) => p.packId === snapshot.rubricPackId);
                // Best-effort history save (widened essayAttempts fields).
                saveOwnAttempt({ ...snapshot, packLabel: pack ? pack.label : '', graded: res });
            } else {
                setDiagnosticId(diagnosticReference(res?.diagnosticId));
                setFailReason((res && res.reason) || 'unavailable'); setGradeState('error');
                if (res?.reason === 'trial_locked') setRevision(r => r + 1);
            }
        } catch (e) {
            setFailReason('unavailable'); setGradeState('error');
        }
    };

    const saveOnly = async () => {
        if (!canMark || draftState === 'saving') return;
        setDraftState('saving');
        try { await saveDraft({ rubricPackId, promptText, essayText, source: 'own',
            packLabel: gradable.find(p => p.packId === rubricPackId)?.label || '' }); setDraftState('saved'); }
        catch { setDraftState('error'); }
    };

    // The feedback shown matches the SUBMITTED snapshot; flag divergence if the
    // user edits after marking (mirrors the in-pack "essay changed" guard).
    const editedSinceMark = gradeState === 'done' && submitted
        && (submitted.rubricPackId !== rubricPackId
            || submitted.promptText !== promptText
            || submitted.essayText !== essayText);

    if (pickerState === 'loading') {
        return <p className="font-serif italic text-midnight-muted text-sm">Checking your packs…</p>;
    }
    if (pickerState === 'error') {
        return null; // picker unavailable -> hide the box (history below unaffected)
    }
    if (trial?.eligible && (!trial.activated || !trial.verified || trial.remaining === 0)) {
        return <div className="border border-midnight-accent/30 bg-midnight-page/60 p-6 space-y-4 text-midnight-text">
            <h3 className="font-display text-midnight-accent">Mark a new essay · locked</h3>
            <p className="text-sm">Verified free accounts can try AI marking twice in total. This does not unlock paid study-pack content. AI feedback is indicative, not an official grade.</p>
            {trial.remaining > 0 ? <>
                <p className="text-sm">{trial.remaining} trial marks available. Verify your email before starting.</p>
                {!trial.verified && <button onClick={verifyEmail} disabled={activating} className="text-midnight-accent underline text-sm">Send verification email</button>}
                <label className="flex gap-2 text-sm"><input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} />I would like to start my two-mark trial.</label>
                <button disabled={!agree || activating} onClick={startTrial} className="px-5 py-3 bg-midnight-accent text-black disabled:opacity-40">{activating ? 'Checking…' : 'Start my free trial'}</button>
            </> : <p className="text-sm">Both trial marks have been used. Unlock a study pack to continue marking.</p>}
            {trialError && <p role="alert" className="text-sm">{trialError}</p>}
            <button onClick={() => navigate('/dashboard')} className="block text-midnight-accent underline">Browse study packs</button>
            {result && <FeedbackView fb={result} />}
        </div>;
    }
    if (gradable.length === 0) {
        // Two distinct zero states: entitled-but-no-rubric-enabled-pack must NOT
        // get a buy upsell (they already paid; AI marking just isn't switched on
        // for their pack yet). Only the truly-unentitled see the purchase CTA.
        const ownsPacks = activePackIds.size > 0;
        return (
            <div className="border border-midnight-accent/30 bg-midnight-page/60 p-6 decorative-corner text-midnight-accent">
                <div className="font-display text-midnight-accent text-xs mb-2 font-semibold">Mark a new essay</div>
                {ownsPacks ? (
                    <p className="font-serif italic text-midnight-muted text-sm leading-relaxed">
                        AI marking isn't switched on for your packs yet — check back soon,
                        or request a Tutor Review for a human mark.
                    </p>
                ) : (
                    <>
                        <p className="font-serif italic text-midnight-muted text-sm leading-relaxed mb-3">
                            You don't have a mark scheme yet — unlock a study pack to have your own answers
                            AI-marked to your exam board's scheme.
                        </p>
                        <button onClick={() => navigate('/dashboard')}
                            className="px-5 py-2.5 bg-midnight-accent text-black font-display text-xs hover:opacity-90 transition-opacity font-semibold">
                            Browse the study packs
                        </button>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="border border-midnight-accent/30 bg-midnight-page/60 p-6 decorative-corner text-midnight-accent space-y-4">
            <div>
                <div className="font-display text-midnight-accent text-xs mb-1 font-semibold">Mark a new essay</div>
                <p className="font-serif italic text-midnight-muted text-xs leading-relaxed">
                    Paste any exam question and your answer — the AI marks it against your pack's rubric.
                    {trial?.eligible ? `${trial.remaining} of your two lifetime trial marks remaining. Trial marking uses free preview materials; include any other required extract with your question.` : `Up to ${Number.isInteger(dailyCap) ? dailyCap : 10} AI marks per day, shared with in-pack marking${Number.isInteger(remaining) ? ` · ${remaining} left today` : ''}.`}
                </p>
            </div>

            <div>
                <label className="block font-display text-midnight-muted text-xs mb-1 font-semibold">Mark scheme</label>
                <select value={rubricPackId} onChange={(e) => setRubricPackId(e.target.value)} disabled={gradeState === 'grading'}
                    className="w-full bg-midnight-bg border border-midnight-accent/30 px-3 py-2 text-midnight-text font-serif text-sm focus:outline-none focus:border-midnight-accent disabled:opacity-50">
                    <option value="">Choose a pack…</option>
                    {gradable.map((p) => <option key={p.packId} value={p.packId}>{p.label}</option>)}
                </select>
                <p className="font-serif italic text-midnight-muted text-xs mt-1">Choose the pack matching your exam board and task. Submit one complete answer at a time.</p>
            </div>

            <div>
                <label className="block font-display text-midnight-muted text-xs mb-1 font-semibold">Your exam question / task</label>
                <textarea rows={2} value={promptText} onChange={(e) => setPromptText(e.target.value)} maxLength={MAX_QUESTION_CHARS} disabled={gradeState === 'grading'}
                    placeholder="e.g. How does the writer present the narrator's changing feelings in the extract?"
                    className="w-full bg-midnight-bg border border-midnight-accent/30 px-3 py-2 text-midnight-text font-serif text-sm focus:outline-none focus:border-midnight-accent resize-y disabled:opacity-50" />
                <p className="font-serif text-midnight-muted text-xs mt-1">Include the board, paper and full question, and name the text(s). Relevant text materials from your selected pack are attached automatically. Paste any required extracts not stored in the pack. Missing essential material may mean feedback without a score.</p>
                {/* Live counter: maxLength truncates pastes SILENTLY, so the cut must be visible. */}
                <p className="font-serif italic text-midnight-muted text-xs mt-1">{promptText.length.toLocaleString()} / {MAX_QUESTION_CHARS.toLocaleString()} characters</p>
            </div>

            <div>
                <label className="block font-display text-midnight-muted text-xs mb-1 font-semibold">Your essay / answer</label>
                <textarea rows={8} value={essayText} onChange={(e) => setEssayText(e.target.value)} maxLength={MAX_ESSAY_CHARS} disabled={gradeState === 'grading'}
                    placeholder="Paste your full answer here…"
                    className="w-full bg-midnight-bg border border-midnight-accent/30 px-3 py-2 text-midnight-text font-serif text-sm focus:outline-none focus:border-midnight-accent resize-y disabled:opacity-50" />
                <p className="font-serif italic text-midnight-muted text-xs mt-1">{essayText.length.toLocaleString()} / {MAX_ESSAY_CHARS.toLocaleString()} characters</p>
            </div>

            <button onClick={mark} disabled={!canMark}
                className="inline-flex items-center gap-2 px-5 py-3 bg-midnight-accent text-black font-display text-xs hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed font-semibold">
                {gradeState === 'grading'
                    ? <span className="inline-block w-3.5 h-3.5 border-2 border-black/40 border-t-black rounded-full animate-spin" />
                    : <IconSparkles className="w-4 h-4" />}
                {gradeState === 'grading' ? 'Marking…' : 'Mark my essay'}
            </button>

            <button type="button" onClick={saveOnly} disabled={!canMark || draftState === 'saving'}
                className="px-4 py-2 border border-midnight-accent/40 text-midnight-accent text-sm disabled:opacity-40">
                {draftState === 'saving' ? 'Saving draft…' : 'Save draft without AI'}
            </button>
            <p role="status" className="text-midnight-muted text-xs">
                {draftState === 'saved' ? 'Draft snapshot saved in your history below. Save again after editing.'
                    : draftState === 'error' ? failureMessage('draft_save_failed')
                        : 'AI marking starts only after a draft snapshot has been saved. Drafts do not use your AI allowance.'}
            </p>

            {gradeState === 'error' && (
                <div className="border border-amber-500/40 bg-amber-500/5 p-4">
                    <p className="font-serif text-midnight-text text-sm leading-relaxed">{failureMessage(failReason)}</p>
                    {diagnosticId && <p className="text-midnight-muted text-xs mt-2 break-all">Support reference: {diagnosticId}</p>}
                </div>
            )}
            {gradeState === 'done' && result && (
                <div className="border border-midnight-accent/25 bg-midnight-page/40 p-4">
                    {/* The save is best-effort (errors swallowed) -- don't assert it
                        succeeded; the live history list below shows it if it did. */}
                    <p className="font-serif italic text-midnight-text text-sm leading-relaxed">
                        Marked — here's your feedback. Saved essays appear in your history below.
                    </p>
                    {editedSinceMark && (
                        <p className="font-serif text-amber-400/90 text-xs leading-relaxed mt-1">
                            You've edited since this was marked — the feedback matches your earlier version.
                        </p>
                    )}
                    <FeedbackView fb={result} />
                </div>
            )}
        </div>
    );
}

const MyEssaysPanel = () => {
    const { currentUser } = useAuth();
    const { attempts, loading, deleteAttempt } = useEssayAttempts();
    const sorted = sortAttemptsNewestFirst(attempts);

    const [openId, setOpenId] = useState(null);     // expanded attempt
    const [confirmId, setConfirmId] = useState(null); // delete-confirm row
    const [deletingId, setDeletingId] = useState(null);
    const [error, setError] = useState(null);

    const signedOut = AUTH_MODE === 'mock' || !currentUser?.uid;

    const remove = async (id) => {
        setError(null); setDeletingId(id);
        try {
            await deleteAttempt(id);
            if (openId === id) setOpenId(null);
            setConfirmId(null);
        } catch {
            setError('Could not delete that essay. Please try again.');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="space-y-6">
            {/* Intro */}
            <div className="border border-midnight-accent/40 bg-midnight-page/60 p-6 decorative-corner text-midnight-accent">
                <div className="font-display text-midnight-accent text-xs mb-2 font-semibold">My Essays</div>
                <p className="font-serif italic text-midnight-muted text-sm leading-relaxed">
                    Saved drafts and AI-marked essays appear here, independently of whether AI feedback succeeds. This is
                    private to you -- only you can read or delete these. AI feedback is formative and indicative, not an
                    official grade.
                </p>
            </div>

            {/* Your Progress: before -> after per redrafted prompt. Above "Mark a
                new essay" so returning users see growth first; skipped while the
                list loads (no nudge flash) and for brand-new accounts (no attempts). */}
            {!signedOut && !loading && <YourProgressCard attempts={attempts} />}

            <AiAllowance kind="grading" />
            {/* Mark a new essay (entitled users; shared daily grade budget) */}
            {!signedOut && <MarkNewEssayBox key={currentUser.uid} />}

            {/* List */}
            {signedOut ? (
                <p className="font-serif italic text-midnight-muted text-sm">Sign in to see your saved essays.</p>
            ) : loading ? (
                <p className="font-serif italic text-midnight-muted text-sm">Loading…</p>
            ) : sorted.length === 0 ? (
                <p className="font-serif italic text-midnight-muted text-sm">
                    No saved essays yet. When you get instant AI feedback on a practice essay, it'll appear here.
                </p>
            ) : (
                <div className="space-y-3">
                    {error && <p className="font-serif text-red-300 text-sm border border-red-500/40 bg-red-500/10 p-3">{error}</p>}
                    {sorted.map((a) => {
                        const expanded = openId === a.id;
                        const fb = a.aiFeedback || {};
                        const draft = isEssayDraft(a);
                        const band = draft ? 'Saved draft · not AI marked' : Number.isInteger(fb.totalMark) && Number.isInteger(fb.totalMax)
                            ? `${fb.totalMark}/${fb.totalMax} (task marks)`
                            : fb.assessmentProfileId === 'feedback_only' ? 'Feedback only'
                                : formatBandRange(fb.indicativeBandRange, fb.mostLikelyBand);
                        return (
                            <div key={a.id} className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="min-w-0">
                                        <div className="font-display text-midnight-text text-sm">{a.promptTitle || a.promptId || 'Practice essay'}</div>
                                        <div className="font-serif italic text-midnight-muted text-xs">
                                            {a.source === 'own'
                                                ? `Own essay · ${a.packLabel || a.rubricPackId || a.packId}`
                                                : a.packId}{band ? ' · ' + band : ''} · {fmt(a.takenAt)}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-3 items-center">
                                    <button onClick={() => setOpenId(expanded ? null : a.id)}
                                        className="font-serif italic text-midnight-muted text-xs hover:text-midnight-accent">
                                        {expanded ? 'Hide essay' : draft ? 'View saved draft' : 'View essay + feedback'}
                                    </button>
                                    {confirmId === a.id ? (
                                        <span className="inline-flex items-center gap-2">
                                            <span className="font-serif italic text-red-300 text-xs">Delete permanently?</span>
                                            <button onClick={() => remove(a.id)} disabled={deletingId === a.id}
                                                className="font-display text-xs text-red-300 hover:text-red-200 disabled:opacity-40 font-semibold">
                                                {deletingId === a.id ? 'Deleting…' : 'Delete'}
                                            </button>
                                            <button onClick={() => setConfirmId(null)} disabled={deletingId === a.id}
                                                className="font-display text-xs text-midnight-muted hover:text-midnight-accent font-semibold">
                                                Cancel
                                            </button>
                                        </span>
                                    ) : (
                                        <button onClick={() => { setError(null); setConfirmId(a.id); }}
                                            className="font-display text-xs text-midnight-muted hover:text-red-400 font-semibold">
                                            Delete
                                        </button>
                                    )}
                                </div>

                                {expanded && (
                                    <div className="mt-3 border-t border-midnight-accent/15 pt-3">
                                        {a.source === 'own' && a.promptText && (
                                            <>
                                                <div className="font-display text-midnight-accent text-xs mb-1 font-semibold">Your question</div>
                                                <p className="font-serif text-midnight-text text-sm leading-relaxed whitespace-pre-wrap border-l-2 border-midnight-accent/30 pl-3 mb-3">
                                                    {a.promptText}
                                                </p>
                                            </>
                                        )}
                                        <div className="font-display text-midnight-accent text-xs mb-1 font-semibold">Your essay</div>
                                        <p className="font-serif text-midnight-text text-sm leading-relaxed whitespace-pre-wrap border-l-2 border-midnight-accent/30 pl-3 max-h-72 overflow-y-auto">
                                            {a.essayText || <span className="italic opacity-60">— no essay text saved —</span>}
                                        </p>
                                        <FeedbackView fb={fb} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyEssaysPanel;
